const SET = 'patron/advocate/SET';
const SET_ENTITLEMENTS = 'patron/advocate/SET_ENTITLEMENTS';

type Avatar = {
  url: string | null;
  color: string;
};

export type User = {
  id: string;
  name: string;
  display_name: string;
  job_title: string | null;
  avatar: Avatar;
  search_attributes: Array<string>;
};

type AvatarWithInitials = Avatar & {
  initials: string;
};

/**
 * Type for the custom font in the program theme
 */
export type CustomFont = {
  css_value: string;
  url: string | null;
};

/**
 * Type for the program theme
 */
export type ProgramTheme = {
  secondary_color: string | null;
  primary_color: string | null;
  theme_color: string | null;
  logo_image_url: string | null;
  icon_image_url: string | null;
  wallpaper_image_url: string | null;
  custom_font: CustomFont;
};

/**
 * Type for mobile app links
 */
export type MobileAppLinks = {
  android_app_url: string;
  ios_app_url: string;
};

/**
 * Type for mobile package info
 */
export type MobilePackageInfo = {
  android_app: {
    package: string;
    version: string;
  };
  ios_app: {
    package: string;
    version: string;
  };
};

/**
 * Type for password validation rules
 */
export type PasswordValidation = {
  text: string;
  rule: string;
};

/**
 * Type for the advocate entitlements program returned from the API
 */
export type AdvocateEntitlementProgram = {
  id: string;
  name: string;
  brand_id: string;
  description: string | null;
  slug: string;
  default_content_channel_id: string | null;
  resource_channel_id: string | null;
  allow_public_feed_access: boolean;
  allow_guest_access: boolean;
  allow_mobile_content_submission: boolean;
  content_sharing_enabled: boolean;
  content_translations_enabled: boolean;
  commenting_default_enabled: boolean;
  commenting_enabled: boolean;
  content_viewed_enabled: boolean;
  hidden_profile_values: string[];
  advanced_profile_enabled: boolean;
  search_bar_in_header_enabled: boolean;
  directory_extra_fields_enabled: boolean;
  anonymous_user_tracking_enabled: boolean;
  comment_think_warning_enabled: boolean;
  comment_think_warning_html: string;
  comment_think_warning_link: string;
  theme: ProgramTheme;
  brand_name: string;
  session_is_sso: boolean | null;
  sp_initiated_slo_enabled: boolean;
  login_url: string;
  root_url: string;
  experience_url: string;
  twig_redirect_url: string;
  root_path: string;
  mobile_app_links: MobileAppLinks;
  mobile_package_info: MobilePackageInfo;
  is_custom_domain: boolean;
  server_base_url: string;
  assistant_server_base_url: string;
  sega_server_base_url: string;
  user_generated_content_topic_limit: string;
  logo_image_url: string | null;
  header_image_url: string | null;
  header_external_image_url: string | null;
  favicon_image_url: string | null;
  assistant_mascot_image_url: string;
  email_preference_url: string | null;
  note_card_colors: string | null;
  assistant: string | null;
  password_rules: string[];
  password_validations: PasswordValidation[];
  strong_password_required: boolean;
  resources_enabled: boolean;
  from_the_community_enabled: boolean;
  patron_deeplink_enabled: boolean;
  v2_notes_enabled: boolean;
  v2_comment_placeholder_enabled: boolean;
  assistant_home_page_experiment_enabled: boolean;
  switch_communities_enabled: boolean;
  images_in_articles_enabled: boolean;
  web_logout_on_session: boolean;
  enable_socialchorus_support: boolean;
  for_you_feed_enabled: boolean;
  comment_sort_enabled: boolean;
  ui_new_foundation_color_icon_typography_enabled: boolean;
  feed_card_views_enabled: boolean;
  org_chart_enabled: boolean;
  mobile_datadog_sso_logging_enabled: boolean;
  expanded_reaction_types_enabled: boolean;
  microapps_third_party_fix_enabled: boolean;
  microapps_updated_login_url_enabled: boolean;
  allow_account_deletion: boolean;
  biometric_login_enabled: boolean;
  content_search_enabled: boolean;
  email_auth_enabled: boolean;
  email_token_auth_enabled: boolean;
  gdpr_compliance_contacts: string | null;
  gdpr_compliance_enabled: boolean;
  hide_program_name_on_login: boolean;
  image_effects_enabled: boolean;
  jailbreak_detection_enabled: boolean;
  max_video_upload_size_in_bytes: string;
  mobile_app_verification_key: string | null;
  profile_edit_name_enabled: boolean;
  sanitized_content_enabled: boolean;
  sso_enabled: boolean;
  user_post_editing_enabled: boolean;
  web_detail_translate_enabled: boolean;
  web_native_sharing_enabled: boolean;
  web_v2_submissions_enabled: boolean;
  private_profile_enabled: boolean;
  qr_code_image_url: string | null;
  org_slug: string;
  domains_allowed_to_embed_experience: string[];
  mobile_database_encryption_required: boolean;
  related_content_enabled: boolean;
  namespace: string;
  program_type: string;
  studio_url: string;
};

/**
 * Type for the advocate entitlements response
 */
export type AdvocateEntitlementsResponse = {
  data: {
    programs: AdvocateEntitlementProgram[];
  };
};

export type AdvocateProfile = {
  id: number;
  avatar: AvatarWithInitials;
  name: string;
  privateProfile: boolean;
  pronouns: string | null;
  profileFields: Array<{
    label: string;
    name: string;
    value: string;
    type: string;
  }>;
  federatedIdentifier: unknown;
  publishingConfig: {
    create_content: Array<unknown>;
    edit_content: Array<string>;
    contributor_channels: Array<number>;
    content_settings: Array<string>;
  };
  role: string;
  hasElevatedRole: boolean;
  email: string;
  hasFallbackNameValue: boolean;
  statusMessage: string | null;
  orgChartEnabled: boolean;
  entitlements?: Array<AdvocateEntitlementProgram>;
};

export type AdvocateGroup = {
  label: string;
  name: string;
  type: string;
  iconUrl: string;
  fields: Array<{
    label: string;
    name: string;
    value: string;
    type: string;
    displayValue: string | null;
  }>;
};

export type AdvocateProfileResponse = {
  data: {
    profile: AdvocateProfile;
    groups: Array<AdvocateGroup>;
  };
};

export type AdvocateSuggestionsSearchResponse = {
  users: Array<User & Record<string, unknown>>;
};

type ContentChannels = {
  id: string;
  name: string;
  description: string;
  background_image_url: string | null;
  cropped_background_image: string | null;
  uploaded_image: {
    width: number | null;
    height: number | null;
    format: string | null;
    filesize: number | null;
    url: string;
  } | null;
  cropped_uploaded_image: {
    width: number | null;
    height: number | null;
    format: string | null;
    filesize: number | null;
    url: string;
  } | null;
  last_published_at: string | null;
  following: boolean;
  default: boolean;
  follower_count: number;
  feed_filters: Array<unknown>;
  can_publish_as_owner: boolean;
  can_submit: boolean;
  last_content_published_at: string | null;
  background_color: string;
  auto_publish: boolean;
  is_recommended: boolean;
  created_at: string;
};

export type AdvocateChannels = {
  content_channels: Array<ContentChannels>;
};

export type AdvocateProfileEditDataResponse = {
  data: {
    profile: AdvocateProfile;
    groups: Array<{
      label: string;
      name: string;
      type: string;
      fields: Array<{
        label: string;
        name: string;
        value: string;
        displayText?: string;
        type: string;
        options: Array<{
          value: string;
          displayText: string;
        }> | null;
        placeholder?: string;
        disabled: boolean;
        required: boolean;
        displayValue?: string;
        helpText?: string;
      }>;
      iconUrl: string;
    }>;
    action: {
      endpoint: string;
      method: string;
    };
  };
};

export type AdvocateOrganizationResponse = {
  data: Array<{
    id: number;
    avatar: AvatarWithInitials;
    name: string;
    jobTitle: string;
    currentUser: boolean;
    level: number;
  }>;
  links: {
    self: string;
  };
};

export type SearchedProfile = Pick<
  User,
  'name' | 'job_title' | 'avatar' | 'display_name'
> & {
  user_id: number;
  email: string | null;
  first_name: string;
  last_name: string;
  postal_code: string | null;
  federated_identifier: string | null;
  phone_number: string | null;
  preferred_name: string | null;
  work_location: string | null;
  start_date: string | null;
  birthdate: string | null;
  private_profile: boolean;
  username: string | null;
  role: string;
  publishing_config: Record<string, unknown>;
};

export type ProfilesSearchResponse = {
  profiles: Array<SearchedProfile>;
  meta: {
    page: {
      size: number;
      number: number;
      total: number;
    };
    total: number;
  };
};

export type UpdateProfileResponse = {
  data: {
    profile: Pick<
      AdvocateProfile,
      | 'avatar'
      | 'email'
      | 'federatedIdentifier'
      | 'hasFallbackNameValue'
      | 'id'
      | 'name'
      | 'privateProfile'
      | 'profileFields'
      | 'pronouns'
      | 'publishingConfig'
      | 'role'
    >;
    groups: Array<unknown>;
  };
};

export type UpdateAdvocateProfilePayload = {
  preferred_name: string;
  first_name: string;
  last_name: string;
  email: string;
  mobile_phone: string;
  phone_number: string;
  timezone: string;
  display_name: string;
  status_message: string;
  pronouns: string;
  preferred_language: string;
  birthdate: string;
};

export type UpdateAdvocatePasswordResponse = {
  message: string;
  data: Record<
    | 'session_id'
    | 'advocate_id'
    | 'brand_id'
    | 'program_id'
    | 'scope'
    | 'service',
    string | null
  >;
};

export type UpdateAdvocateAvatar = Record<'avatar_url', string>;

export default {
  SET,
  SET_ENTITLEMENTS,
};
